import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { putTaricUpdate } from '../../api/taric';
import Modal from '../../components/Modal';
import { RhfTextField } from '../../components/Inputs';
import { addCommodityCodeSchema, getNormalizedCommodityCode } from './addCommodityCodeSchema';

const AddCommodityCodeModal = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [addedCode, setAddedCode] = useState('');
  const [resultModal, setResultModal] = useState(null);

  const { control, handleSubmit, reset, setValue, formState } = useForm({
    resolver: zodResolver(addCommodityCodeSchema),
    defaultValues: {
      commodityCode: '',
    },
    mode: 'onChange',
  });

  const addCommodityCode = useMutation({
    mutationFn: ({ commcode }) =>
      putTaricUpdate({
        commcode,
        expCommodityCode: commcode.substring(0, 8),
        add: true,
      }),
    onSuccess: (_, { commcode }) => {
      setAddedCode(commcode);
      setResultModal({
        type: 'success',
        title: 'Success',
        message: 'Commodity code successfully added to the database.',
      });
    },
    onError: (err) => {
      let message = 'Invalid commodity code.';

      try {
        const parsed = JSON.parse(err.message);
        message = parsed.error || message;
      } catch {
        message = err.message || message;
      }

      setResultModal({
        type: 'error',
        title: 'Error',
        message,
      });
    },
  });

  const loading = addCommodityCode.isPending;

  const handleClose = () => {
    if (loading) return;
    reset();
    setAddedCode('');
    setResultModal(null);
    onClose();
  };

  const handleCommodityCodeChange = (event) => {
    const value = event.target.value;
    if (/^[\d\s]*$/.test(value)) {
      setValue('commodityCode', value, { shouldValidate: true, shouldDirty: true });
    }
  };

  const onSubmit = ({ commodityCode }) => {
    const commcode = getNormalizedCommodityCode(commodityCode);
    addCommodityCode.mutate({ commcode });
  };

  const handleResultModalClose = () => {
    const wasSuccess = resultModal?.type === 'success';
    setResultModal(null);

    if (wasSuccess) {
      reset();
      setAddedCode('');
      onClose();
      onSuccess?.();
    }
  };

  const handleViewCommodityCode = () => {
    if (addedCode) {
      navigate(`/taric-tool/${addedCode}`);
    }
  };

  return (
    <>
      <Modal
        open={open && !resultModal}
        onClose={handleClose}
        onConfirm={handleSubmit(onSubmit)}
        title="Add New Commodity Code"
        showCloseButton
        maxWidth="sm"
        secondaryButton={{
          text: 'Cancel',
          hidden: false,
          disabled: loading,
          variant: 'primary',
          color: 'primary',
        }}
        primaryButton={{
          text: loading ? 'Adding...' : 'Add',
          hidden: false,
          disabled: !formState.isValid || loading,
          variant: 'primary',
          color: 'secondary',
          sx: { minWidth: 100 },
        }}
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Typography sx={{ mb: 2 }}>Enter a valid 10-digit commodity code to add to the database.</Typography>
          <Box sx={{ py: 2 }}>
            <RhfTextField
              name="commodityCode"
              control={control}
              placeholder="Enter 10-digit commodity code"
              autoComplete="off"
              disabled={loading}
              onChange={handleCommodityCodeChange}
            />
          </Box>
        </Box>
      </Modal>

      <Modal
        open={!!resultModal}
        onClose={handleResultModalClose}
        onConfirm={handleResultModalClose}
        title={resultModal?.title}
        maxWidth="sm"
        secondaryButton={{ text: 'Cancel', hidden: true }}
        primaryButton={{ text: 'OK', hidden: false, variant: 'primary', color: 'secondary' }}
      >
        <Typography>{resultModal?.message}</Typography>
        {resultModal?.type === 'success' && addedCode && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              View commodity code:
            </Typography>
            <Link
              component="button"
              type="button"
              onClick={handleViewCommodityCode}
              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
            >
              {addedCode}
            </Link>
          </Box>
        )}
      </Modal>
    </>
  );
};

export default AddCommodityCodeModal;
